import { Controller } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { noop } from "lodash-es"
import { CountrySelect, useReusableWizard } from "@oysterhr/feature-components"

import { config } from "@oysterhr/core"

import { FormLabel, TextInput, Select, Text } from "@oysterhr/ui"
import { NavigationButtons } from "~/shared/components/NavigationButtons"
import { allCountries } from "@oysterhr/common"

import { useParams } from "react-router-dom"
import { useAccountAspect } from "@oysterhr/identity"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import {
  ACCOUNT_HOLDER_NAME_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  ROUTING_NUMBER_LENGTH,
} from "../../../../constants"
import { useAchPaymentMethodForm } from "../../hooks/useAchPaymentMethodForm"
import { useNewCompanyPaymentMethods } from "../../../../../hooks/useNewCompanyPaymentMethods"
import { PaymentEngagementType } from "../../../../../enums"
import { BankAccountEnumType } from "@oysterhr/schema"
import { BankAccountTypeModal } from "../../../../components/BankAccountTypeModal"

export const BankDetails = () => {
  const { t } = useTranslation("company_interface/AddStripeAchPage")
  const { paymentFlow } = useParams()
  const { goToNext } = useReusableWizard()
  const { accountAspect: companyId } = useAccountAspect()

  const isContractorFlow = !paymentFlow || paymentFlow === PaymentEngagementType.Contractor

  const {
    onNext,
    onSubmit,
    handleSubmit,
    loading,
    formIsReady,
    control,
    register,
    setValue,
    formState: { errors },
  } = useAchPaymentMethodForm({
    onSuccess: goToNext,
  })

  useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "cache-and-network",
    onCompleted: data => {
      const customerBankAccount = data?.company.customerBankAccount
      if (
        isContractorFlow ||
        customerBankAccount?.__typename !== PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH
      )
        return

      setValue("accountHolderName", customerBankAccount.accountHolderName)
      setValue("accountNumber", customerBankAccount.accountNumber)
      setValue("country", customerBankAccount.countryCode)
      setValue("currencyCode", customerBankAccount.currencyCode)
      setValue("routingNumber", customerBankAccount.routingNumber)
    },
  })

  if (config.nonProductionEnvironment) {
    console.log("TEST DATA - Routing number: 110000000 Account number: 000123456789")
  }

  const action = isContractorFlow ? onNext : onSubmit

  return (
    <form onSubmit={handleSubmit(action)} className="mt-8  p-2">
      <fieldset className="flex flex-col gap-4">
        <Text size="large">{t("fillInDetails")}</Text>
        <div>
          <Controller
            name={"country"}
            control={control}
            render={({ field: { value, name } }) => (
              <CountrySelect
                countries={allCountries}
                name={name}
                label={t("country")}
                value={value}
                onChange={noop}
                disabled
              />
            )}
          />
        </div>
        <div>
          <Controller
            name={"currencyCode"}
            control={control}
            render={({ field: { name, value } }) => (
              <>
                <FormLabel label={t("currency")} htmlFor={`currency-select-${name}-label`} />
                <Select disabled value={value} onChange={noop} />
              </>
            )}
          />
        </div>
        <div>
          <TextInput
            {...register("routingNumber")}
            label={<FormLabel label={t("routingNumber")} />}
            error={errors?.routingNumber?.message}
            maxLength={ROUTING_NUMBER_LENGTH}
          />
        </div>
        <div>
          <TextInput
            {...register("accountNumber")}
            label={<FormLabel label={t("accountNumber")} />}
            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
            error={errors?.accountNumber?.message}
          />
        </div>
        <div>
          <TextInput
            {...register("accountHolderName")}
            label={<FormLabel label={t("accountHolderName")} />}
            maxLength={ACCOUNT_HOLDER_NAME_MAX_LENGTH}
            error={errors?.accountHolderName?.message}
          />
        </div>
        <div>
          <Controller
            name="bankAccountType"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Select
                  name="bankAccountType"
                  value={value}
                  label={
                    <FormLabel
                      label={t("bankAccountType.label")}
                      endAdornment={<BankAccountTypeModal />}
                    />
                  }
                  onChange={val => onChange(val as BankAccountEnumType)}
                  error={error?.message}
                  placeholder={t("bankAccountType.placeholder")}
                >
                  <Select.Option value={BankAccountEnumType.Individual}>
                    {t("bankAccountType.options.individual")}
                  </Select.Option>
                  <Select.Option value={BankAccountEnumType.Business}>
                    {t("bankAccountType.options.business")}
                  </Select.Option>
                </Select>
              )
            }}
          />
        </div>
      </fieldset>
      <NavigationButtons
        isFirst
        disabled={!formIsReady}
        loading={loading}
        analyticsBase="ach-bank-details"
      />
    </form>
  )
}
